import { useMemo, useState } from 'react';

import { ApolloQueryResult } from '@apollo/client';

import { TGoodsMinimal } from 'types';

import {
  ETabs,
  IWarehouseManagerReturnedData,
  SelectedGood,
  IGood,
  TGoodMap,
} from './interfaces';
import { useChangeGoodsQuantity } from './useChangeGoodsQuantity';

export const useWarehouseManager = (
  goods?: IGood[],
  onClose?: () => void,
  tab?: ETabs,
  refetch?: () => Promise<ApolloQueryResult<{ goods: Array<TGoodsMinimal> }>>
): IWarehouseManagerReturnedData => {
  const [selectedGoods, setSelectedGoods] = useState<SelectedGood[]>([]);
  const [successCount, setSuccessCount] = useState<number>(0);
  const [isProcessingWarehouse, setIsProcessingWarehouse] = useState(false);
  const { changeQuantity, loading } = useChangeGoodsQuantity();

  const goodsMap = useMemo<TGoodMap>(() => {
    const map: TGoodMap = {};

    if (goods) {
      goods.forEach((good) => (map[good.id] = good));
    }

    return map;
  }, [goods]);

  const getGoodsTitleForId = (id: number): string => {
    return goods?.find((good) => good.id === id)?.title || '';
  };

  const handleAddGood = (value: number | null): void => {
    if (value !== null) {
      setSelectedGoods((prev) => [
        ...prev,
        {
          value,
          title: getGoodsTitleForId(value),
          quantity: 0,
          comment: '',
          amount: goods?.find((g) => g.id === value)?.amount || 0,
        },
      ]);
    }
  };

  const handleUpdateGood = (
    index: number,
    key: keyof SelectedGood,
    newValue: number | string | null
  ): void => {
    setSelectedGoods((prev) => {
      const updatedGoods = [...prev];

      if (key === 'value' && typeof newValue === 'number') {
        updatedGoods[index] = { ...updatedGoods[index], [key]: newValue };

        if (goodsMap[newValue]) {
          updatedGoods[index] = {
            ...updatedGoods[index],
            amount: goodsMap[newValue].amount,
            title: goodsMap[newValue].title,
          };
        }
      } else if (key !== 'value') {
        updatedGoods[index] = { ...updatedGoods[index], [key]: newValue };
      }

      return updatedGoods;
    });
  };

  const handleRemoveGood = (index: number): void => {
    setSelectedGoods((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFormSubmit = async (): Promise<void> => {
    const failedGoods: SelectedGood[] = [];
    setSuccessCount(0);
    setIsProcessingWarehouse(true);

    const delay = (ms: number): Promise<void> =>
      new Promise((resolve) => setTimeout(resolve, ms));

    try {
      for (const good of selectedGoods) {
        try {
          await changeQuantity({
            goodId: good.value as number,
            quantity: good.quantity as number,
            comment: good.comment,
            operation: tab === ETabs.ADD ? 'ADD' : 'DISPOSAL',
          });
          setSuccessCount((prev) => prev + 1);
        } catch {
          failedGoods.push({
            title: good.title,
            value: good.value,
            quantity: good.quantity,
            comment: good.comment,
            amount: goods?.find((g) => g.id === good.value)?.amount || 0,
          });
        }

        await delay(300);
      }

      setSelectedGoods([...failedGoods]);

      if (failedGoods.length === 0) {
        onClose && onClose();
      }
    } finally {
      refetch && refetch();
      setIsProcessingWarehouse(false);
    }
  };
  return {
    selectedGoods,
    handleAddGood,
    handleUpdateGood,
    handleRemoveGood,
    handleFormSubmit,
    setSelectedGoods,
    successCount,
    loading,
    isProcessingWarehouse,
  };
};
